import Cookie from 'js-cookie';
import { parse } from 'psl';
import { isMobile } from 'react-device-detect';

import { ViewOption } from 'app/components/v3/components/Table/types';
import { keys } from 'app/config/keys';
import { nowInSeconds } from 'app/utils/lib/dates';

import { TokenExpirationResponse } from './apiClient/generated/types';

// Local Typings
export type GridListViewName =
  | 'adminEditPayorPaymentTerms'
  | 'adminEditPayorSubscriptionFees'
  | 'payeeInvoicesView'
  | 'payeePaperworkView'
  | 'payeePaymentsView'
  | 'payorCreditNotesView'
  | 'payorInvoicesView'
  | 'payorLoadsView'
  | 'payorPaperworkView'
  | 'payorPaymentsView'
  | 'payorRelationshipsView';

type SideNavPreference = 'closed' | 'open';

interface OktaOAuthRedirectParams {
  clientId: string;
  ignoreSignature: boolean;
  nonce: string;
  responseType: string;
  scopes: string[];
  state: string;
  urls: OktaOAuthRedirectParamsUrls;
}

interface OktaOAuthRedirectParamsUrls {
  authorizeUrl: string;
  issuer: string;
  logoutUrl: string;
  revokeUrl: string;
  tokenUrl: string;
  userinfoUrl: string;
}

// Local Variables
const adminPanelSessionCookieName = 'adminPanelSession';
const alertCookieName = 'alertMessage';
const allowCookieAcceptValue = 'allowed';
const allowCookieName = 'allowCookies';
const allowCookieRejectValue = 'rejected';
const companyIdCookieName = 'companyId';
const hasVisitedWithinThirtyMinutesCookieName = 'hasVisitedWithinThirtyMinutes';
const isTest = keys.NODE_ENV === 'test';
const lastInteractionCookieName = 'lastInteraction';
const optOutOfMobileAppName = 'mobileAppOptOut';
const optOutOfModernBrowserName = 'modernBrowserOptOut';
const rememberMeCookieName = 'rememberMe';
const selectCarrierAdvertisementPreferenceName = 'isScCollapsed';
const selectCarrierAdvertisementSuccessExp = (companyId: string) => `scSuccessExp-${companyId}`;
const sessionCurrencyTypeCookieName = 'sessionCurrencyTypeId';
const sideNavPreferenceCookieName = 'sideNavPreference';
const tokenExpCookieName = 'tokenExp';
const verificationCodeCookieName = 'verificationCode';

const defaultViewPreferences: Record<GridListViewName, ViewOption> = {
  adminEditPayorPaymentTerms: 'list',
  adminEditPayorSubscriptionFees: 'grid',
  payeeInvoicesView: 'grid',
  payeePaperworkView: 'grid',
  payeePaymentsView: 'list',
  payorCreditNotesView: 'grid',
  payorInvoicesView: 'grid',
  payorLoadsView: 'list',
  payorPaperworkView: 'list',
  payorPaymentsView: 'list',
  payorRelationshipsView: 'list',
};

// eslint-disable-next-line @stylistic/max-len
export const testCookie = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJodHRwOi8vc2NoZW1hcy54bWxzb2FwLm9yZy93cy8yMDA1LzA1L2lkZW50aXR5L2NsYWltcy9uYW1lIjoic2Ftc3RldmVuc0B0cml1bXBocGF5LmNvbSIsIlVJZCI6IjE2ODg4IiwiVUd1aWQiOiIxMjM0IiwiQXBpIjoiMTIzNCIsIkNJZCI6IjEyMzQiLCJDR3VpZCI6IjEyMzQiLCJUSWQiOiIyIiwiRk5hbWUiOiJTYW0gU3RldmVucyIsIlJvbGVzIjoiMSwyLDUsMTQsMTUsMjEsMjMsMjQsMjYsMjgsMjkiLCJleHAiOjE1NjU5ODM5OTYsImlzcyI6Imh0dHBzOi8vcmVhY3QuZGV2LXRyaXVtcGhwYXkuY29tLyJ9.zbI3Hofbl1kwkekRt_zquZeSi6AMrVsd8KaHd5KQCUI';

function getCurrentDomain(): string | undefined {
  if (window) {
    const parsedHostName = parse(window.location.hostname);
    if ('error' in parsedHostName || !parsedHostName.domain) {
      return undefined;
    }

    return parsedHostName.domain;
  }

  return undefined;
}

export const getTokenExpFromResponse = (data: TokenExpirationResponse | undefined) => {
  const now = nowInSeconds();

  const seconds = data?.seconds ?? 0;

  return seconds <= 0 ? 0 : Math.floor(now + seconds);
};

export const clearVerificationCodeCookie = () => {
  Cookie.remove(verificationCodeCookieName,
    { domain: getCurrentDomain() });
};

export const areCookiesAccepted = () => Cookie.get(allowCookieName) === allowCookieAcceptValue;

// Exports
export const getSessionCurrencyTypeId = () => Cookie.get(sessionCurrencyTypeCookieName);
export const setSessionCurrencyTypeId = (id: string) => Cookie.set(sessionCurrencyTypeCookieName,
  id);
export const clearSessionCurrencyTypeId = () => {
  Cookie.remove(sessionCurrencyTypeCookieName);
};
export const setAllowCookieAccept = () => Cookie.set(
  allowCookieName,
  allowCookieAcceptValue,
  {
    domain: getCurrentDomain(),
    expires: 365,
  },
);
export const setAllowCookieReject = () => Cookie.set(
  allowCookieName,
  allowCookieRejectValue,
  { domain: getCurrentDomain() },
);
export const refreshAllowCookieIfAccepted = () => {
  // This expires after one year. Refresh this expiration date each time they visit the page
  if (areCookiesAccepted()) {
    setAllowCookieAccept();
  }
};
export const areCookiesRejected = () => Cookie.get(allowCookieName) === allowCookieRejectValue;
export const getOptOutModernBrowserCookie = () => Cookie.get(optOutOfModernBrowserName);
export const getOptOutMobileAppCookie = () => Cookie.get(optOutOfMobileAppName);
export const setOptOutMobileAppCookie = () => {
  if (!areCookiesAccepted()) {
    return null;
  }

  return Cookie.set(
    optOutOfMobileAppName,
    'true',
    {
      domain: getCurrentDomain(),
      expires: 30,
    },
  );
};
export const setOptOutModernBrowserCookie = () => {
  if (!areCookiesAccepted()) {
    return null;
  }

  return Cookie.set(
    optOutOfModernBrowserName,
    'true',
    {
      domain: getCurrentDomain(),
      expires: 30,
    },
  );
};
export const getRememberMe = () => {
  if (isTest) {
    return testCookie;
  }

  return Cookie.get(rememberMeCookieName);
};
export const setRememberMe = (rememberMe: boolean,
  username: string) => {
  if (!areCookiesAccepted()) {
    return null;
  }

  if (isTest) {
    return testCookie;
  }

  if (rememberMe) {
    Cookie.set(
      rememberMeCookieName,
      username,
      {
        domain: getCurrentDomain(),
        expires: 365,
      },
    );
  } else {
    Cookie.remove(rememberMeCookieName,
      { domain: getCurrentDomain() });
  }
};
export const setSelectCarrierAdvertisementPreference = (isCollapsed: boolean) => {
  if (!areCookiesAccepted()) {
    return null;
  }

  Cookie.set(selectCarrierAdvertisementPreferenceName,
    isCollapsed.toString());
};
export const getSelectCarrierAdvertismentPreference = () => {
  return Cookie.get(selectCarrierAdvertisementPreferenceName) === 'true';
};

export const setSelectCarrierAdvertisementSuccessExp = (companyId: string) => {
  if (!areCookiesAccepted()) {
    return null;
  }

  Cookie.set(
    selectCarrierAdvertisementSuccessExp(companyId),
    'true',
    { expires: 1 },
  );
};
export const getSelectCarrierAdvertisementSuccessExp = (companyId: string) => {
  return Cookie.get(selectCarrierAdvertisementSuccessExp(companyId));
};

export const setViewPreference = (viewPreferenceName: GridListViewName, viewOption: ViewOption): void => {
  Cookie.set(
    viewPreferenceName,
    viewOption,
    {
      expires: 365,
    },
  );
};
export const getViewPreference = (viewPreferenceName: GridListViewName): ViewOption => isMobile
  ? 'grid'
  : Cookie.get(viewPreferenceName) as undefined | ViewOption
    ?? defaultViewPreferences[viewPreferenceName];
export const setHasVisitedWithinThirtyMinutes = () => {
  if (!areCookiesAccepted()) {
    return;
  }

  const now = Date.now();
  const oneMinute = 60000;
  const thirtyMinutes = oneMinute * 30;
  const nowPlusThirtyMinutes = now + thirtyMinutes;
  const thirtyMinutesInFuture = new Date(nowPlusThirtyMinutes);

  return Cookie.set(
    hasVisitedWithinThirtyMinutesCookieName,
    'true',
    {
      expires: thirtyMinutesInFuture,
    },
  );
};
export const getHasVisitedWithinThirtyMinutes = () => Cookie.get(hasVisitedWithinThirtyMinutesCookieName);

export const setAlertMessage = (message: string) => Cookie.set(alertCookieName,
  message);
export const getAlertMessage = () => Cookie.get(alertCookieName);
export const clearAlertMessage = () => {
  Cookie.remove(alertCookieName);
};

export const setSideNavPreference = (preference: SideNavPreference) => {
  if (!areCookiesAccepted()) {
    return;
  }

  return Cookie.set(
    sideNavPreferenceCookieName,
    preference,
    {
      expires: 365,
    },
  );
};
export const getSideNavPreference = (): SideNavPreference | undefined =>
  Cookie.get(sideNavPreferenceCookieName) as SideNavPreference | undefined;

export const setAdminPanelSession = () => Cookie.set(adminPanelSessionCookieName,
  'true');
export const getAdminPanelSession = () => Cookie.get(adminPanelSessionCookieName);
export const removeAdminPanelSession = () => {
  Cookie.remove(adminPanelSessionCookieName);
};

export const getCompanyId = () => Cookie.get(companyIdCookieName);
export const removeCompanyId = () => {
  Cookie.remove(companyIdCookieName);
};

export const setTokenExp = (exp: number) => {
  if (!areCookiesAccepted()) {
    return null;
  }

  return Cookie.set(tokenExpCookieName,
    `${exp}`);
};
export const getTokenExp = () => {
  const tokenExp = Cookie.get(tokenExpCookieName);

  if (tokenExp && !isNaN(Number(tokenExp))) {
    return Number(tokenExp);
  }

  return 0;
};
export const removeTokenExp = () => {
  Cookie.remove(tokenExpCookieName);
};

export const setLastInteractionTimestamp = () => {
  if (!areCookiesAccepted()) {
    return null;
  }

  return Cookie.set(lastInteractionCookieName,
    `${nowInSeconds()}`);
};

export const getLastInteractionTimestamp = () => {
  const timestampString = Cookie.get(lastInteractionCookieName);

  if (timestampString && !isNaN(Number(timestampString))) {
    return Number(timestampString);
  }

  return 0;
};

export const hasUserInteractedWithinMinutes = (minutes: number): boolean => {
  const lastInteraction = getLastInteractionTimestamp();
  const now = nowInSeconds();
  const thresholdInSeconds = minutes * 60;

  return now - lastInteraction < thresholdInSeconds;
};

export const isTokenLifeBeyondThreshold = (tokenLifeRemainingInSeconds: number) => {
  const minutes = keys.SESSION_INACTIVITY_THRESHOLD_IN_MINUTES;

  const secondsUntilThreshold = tokenLifeRemainingInSeconds - minutes * 60;

  return secondsUntilThreshold <= 0;
};

export const isTokenMinutesFromExpiring = (): boolean => {
  const tokenExp = getTokenExp();
  const secondsUntilExp = tokenExp - nowInSeconds();

  return isTokenLifeBeyondThreshold(secondsUntilExp);
};

export const getOktaOAuthRedirectParams = (): null | OktaOAuthRedirectParams => {
  const value = Cookie.get('okta-oauth-redirect-params');

  return value ? JSON.parse(value) as OktaOAuthRedirectParams : null;
};
